import React from 'react';
import { Link } from 'components/link';
import SendIcon from 'assets/svg/send.svg';
import PhoneIcon from 'assets/svg/phone-new.svg';
import CheckIcon from 'assets/svg/ok-check.svg';
import * as Styled from './styled';
import { useLocale } from 'locale';
import { AgentContactProps } from 'components/contact/types';
import { PoiId } from 'utils/entities';
import { createPhoneURI } from 'hooks/useDisplayPhone';
import OfficePlaceholderIcon from 'assets/svg/office-placeholder.svg';
import { TextField } from 'ds/components/input/TextField';
import { Poi } from 'utils/marketplaceRoutes';
import { createMortgageValidationFn } from 'components/madad/contact-modal/utils';
import { ContactAgentModalSource } from 'store/state/app';
import { SubmitContactAgentPayload, ContactAgentSubmitterSource } from 'store/state/app/payloads';
import { IContactPageSubmitVariables } from 'store/sagas/apiService/types';
import AgentPlaceholder from 'assets/svg/user-placeholder/agent.svg';
import { noop } from 'lodash';
import { Route } from 'config/routes';
import { useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import { useScreenBreakpoint } from 'consts/breakpoints';
import _ from 'lodash';
import { SpacingScale } from 'ds/theme/spacing';
import TermsLine from 'components/terms-line';

interface SideBarAgentContactProps extends AgentContactProps {
  id: PoiId;
  sidePaddingSpacing?: SpacingScale;
  source: ContactAgentModalSource;
  onContactAgentInputClick: (eventName: string, poi: Poi) => void;
  onContactPageSubmit: (payload: SubmitContactAgentPayload) => void;
  handleSubmitEvent?: (hasErrors: boolean, formValues: IContactPageSubmitVariables, source?: string) => void;
  onCallClickEvent?: (isVisible: boolean, formValues: IContactPageSubmitVariables, source?: string) => void;
}

interface State {
  userName: string;
  userPhone: string;
  formErrors: {
    phone?: string;
    message?: string;
    name?: string;
  };
}

export const SideBarAgentContact: React.FC<SideBarAgentContactProps> = (props) => {
  const {
    id,
    poi,
    poiType,
    officeId,
    agentId,
    source,
    isLeadingAgent,
    officeLogo,
    agentLogo,
    contactValues,
    agentName,
    officeName,
    hideAgentInfo,
    showOnlyOffice,
    isExclusive,
    phone,
    isContacted,
    onCallClickEvent = noop,
    handleSubmitEvent = noop,
    onContactPageSubmit,
    onContactAgentInputClick,
    sidePaddingSpacing,
    licenseId = null,
  } = props;

  const { t } = useLocale();
  const screenBreakpoint = useScreenBreakpoint();
  const isMobile = screenBreakpoint < 3;
  const constructImageURI = useImageUrlBuilder();
  const [ phoneNumberIsVisible, setPhoneNumberIsVisible ] = React.useState(false);
  const [ state, setState ] = React.useState<State>({ userName: contactValues.name, userPhone: contactValues.phone, formErrors: {} });

  const handlePhoneSubmit = (e: React.MouseEvent<HTMLElement>) => {
    onCallClickEvent(isMobile || phoneNumberIsVisible, { phone: state.userPhone, name: state.userName, isMortgageConsult: false, id, type: poiType }, source);

    if (!phoneNumberIsVisible && !isMobile) {
      e.preventDefault();
      setPhoneNumberIsVisible(true);
    }
  };

  const onPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => setState({ ...state, userPhone: e.currentTarget.value });
  const onMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => setState({ ...state, userName: e.currentTarget.value });


  const validate = React.useCallback(createMortgageValidationFn({
    phone: {
      invalid: t('contactAgentModal.form.phone.invalid'),
      required: t('contactAgentModal.form.phone.required'),
    },
    message: {
      required: t('contactAgentModal.form.message.required'),
    },
    name: {
      invalid: t('contactAgentModal.form.name.invalid'),
      tooLong: t('contactAgentModal.form.name.tooLong'),
    },
  }, contactValues.name), [ t, contactValues.name ]);

  const onSubmit = () => {
    const errors = validate({
      phone: state.userPhone,
      name: state.userName,
    });
    const formValues = {
      phone: state.userPhone,
      name: state.userName,
      isMortgageConsult: false,
      type: poiType,
      id,
      source,
    };
    setState({ ...state, formErrors: errors });

    let hasErrors = false;

    if (errors.phone || errors.name) {
      hasErrors = true;
    }

    handleSubmitEvent(hasErrors, formValues, source);

    if (hasErrors) return;

    onContactPageSubmit({
      key: formValues.id,
      variables: formValues,
      source: ContactAgentSubmitterSource.Form,
    });
  };

  return (
    <Styled.Root sidePaddingSpacing={sidePaddingSpacing}>
      <Styled.FormTitle weight="bold">{t('contactForm.contact.form.withMortgage.mobile.title', { isContacted: false })}</Styled.FormTitle>
      <Styled.InfoBox>
        <Styled.LogoWrapper>
          <Link routeName={Route.OfficePage} routeParams={{ id: officeId, source: 'listing_page' }}>
            {officeLogo ? <Styled.OfficeLogo imageURL={constructImageURI(officeLogo)} /> : <OfficePlaceholderIcon width={80} height={80} />}
          </Link>
          <Link className="agent-logo" routeName={Route.AgentPage} routeParams={{ id: agentId, source: 'listing_page' }}>
            {agentLogo ? <Styled.AgentLogo imageURL={constructImageURI(agentLogo)} /> : <AgentPlaceholder width={80} height={80} />}
            {isLeadingAgent ? <Styled.LeadingLabel small weight="bold" >{t('leading.agent.contact.form.label')}</Styled.LeadingLabel> : null}
          </Link>
        </Styled.LogoWrapper>
        <Styled.TextWrapper>
          <Link routeName={showOnlyOffice ? Route.OfficePage : Route.AgentPage} routeParams={{ id: showOnlyOffice ? officeId : agentId, source: 'listing_page' }}>
            <Styled.AgentName weight="bold"  data-auto="poc-name">
              {showOnlyOffice ? officeName : agentName}
            </Styled.AgentName>
          </Link>
          {hideAgentInfo ? null : (
            <div style={{ textAlign: 'center' }}>
              <Styled.AgentText>
                {t('unitPage.contactAgentHebrewExclusive', { exclusive: isExclusive })}
                {licenseId ? (
                  <>
                    <Styled.AgentTextDivide />
                    <Styled.LicenseLabel small>
                      {t('unitPage.contactAgentLicense', { licenseId })}
                    </Styled.LicenseLabel>
                  </>
                ) : null}
              </Styled.AgentText>
            </div>
          )}
        </Styled.TextWrapper>
      </Styled.InfoBox>

      <div style={{ width: '100%' }}>
        <Styled.PhoneButton
          data-auto="phone-number-button"
          size="extraLarge"
          fullWidth
          weight="medium"
          onClick={handlePhoneSubmit}
          href={createPhoneURI(phone)}
        >
          <PhoneIcon />
          {phoneNumberIsVisible ? phone : (isMobile ? t('contactAgentModal.callButton') : t('contactAgentModal.callButton.desktop'))}
        </Styled.PhoneButton>
      </div>

      <Styled.HintTitle weight="medium">
        {t('contactForm.contact.sidebar.agent.hint')}
      </Styled.HintTitle>
      <Styled.FormBody>
        {isContacted ? (
          <Styled.ContactedWrapper>
            <CheckIcon width={24} height={24} />
            {t('contactAgentModal.button.disabled.modeB.static')}
          </Styled.ContactedWrapper>
        ) : (
          <>
            <TextField
              fullWidth
              data-auto="name-input"
              hasError={Boolean(state.formErrors.name)}
              errorMessage={state.formErrors.name}
              value={state.userName}
              onChange={onMessageChange}
              placeholder={t('contactForm.contact.name')}
              onClick={() => onContactAgentInputClick('property_lead_form_messege_box_click', poi)}
            />
            <TextField
              fullWidth
              value={state.userPhone}
              hasError={Boolean(state.formErrors.phone)}
              data-auto="phone-input"
              errorMessage={state.formErrors.phone}
              onChange={onPhoneChange}
              placeholder={t('contactForm.contact.form.placeholder.phone')}
              onClick={() => onContactAgentInputClick('property_lead_form_phone_text_box_click', poi)}
            />
            <Styled.SubmitButton onClick={onSubmit} data-auto="submit-lead-button">
              <SendIcon />
            </Styled.SubmitButton>
          </>
        )}
      </Styled.FormBody>
      <TermsLine />
    </Styled.Root>
  );
};
